import classNames from 'classnames';
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ComponentType,
  forwardRef,
  type ReactElement,
  type ReactNode,
  type SVGProps,
} from 'react';
import Skeleton from 'react-loading-skeleton';

import { Spinner } from '../spinner/spinner';
import { type ButtonVariant } from './buttonVariant';

import styles from './button.module.scss';

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'prefix'> & {
  children?: ReactNode;
  className?: string;
  dataTestId?: string;
  download?: string;
  Icon?: ComponentType<SVGProps<SVGSVGElement>>;
  isExternal?: boolean;
  isInverted?: boolean;
  isLoading?: boolean;
  isSmall?: boolean;
  isSubmitting?: boolean;
  LinkComponent?: ComponentType<AnchorHTMLAttributes<HTMLAnchorElement>>;
  prefix?: ReactElement;
  suffix?: ReactElement;
  target?: string;
  to?: string;
  variant?: ButtonVariant;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      dataTestId,
      disabled,
      download,
      Icon,
      isExternal = false,
      isInverted,
      isLoading = false,
      isSmall,
      isSubmitting = false,
      LinkComponent,
      onClick,
      prefix,
      suffix,
      target,
      to,
      type = 'button',
      variant,
      ...props
    },
    ref
  ) => {
    if (isLoading) {
      return <Skeleton height={40} width={160} {...(Icon && { circle: true, width: 40 })} />;
    }

    const PARENT_CLASSNAME = 'button';

    const sharedButtonProps = {
      ...props,
      'aria-busy': isSubmitting || undefined,
      'data-testid': dataTestId,
      disabled: disabled || isSubmitting,
      onClick,
      ref,
      type,
    };

    if (Icon) {
      return (
        <button
          {...sharedButtonProps}
          className={classNames(className, styles[`${PARENT_CLASSNAME}--icon`], {
            [styles[`${PARENT_CLASSNAME}--small`]]: isSmall,
          })}
        >
          {isSubmitting ? <Spinner /> : <Icon />}
        </button>
      );
    }

    const resolvedPrefix = isSubmitting ? <Spinner /> : prefix;

    const buttonClasses = classNames([
      styles[PARENT_CLASSNAME],
      {
        [styles[`${PARENT_CLASSNAME}--${variant}`]]: variant,
        [styles[`${PARENT_CLASSNAME}--has-prefix`]]: resolvedPrefix !== undefined,
        [styles[`${PARENT_CLASSNAME}--has-suffix`]]: suffix !== undefined,
        [styles[`${PARENT_CLASSNAME}--inverted`]]: isInverted,
        [styles[`${PARENT_CLASSNAME}--small`]]: isSmall,
      },
      className,
    ]);

    const content = (
      <>
        {resolvedPrefix && (
          <div className={styles[`${PARENT_CLASSNAME}__prefix`]}>{resolvedPrefix}</div>
        )}
        {children}
        {suffix && <div className={styles[`${PARENT_CLASSNAME}__suffix`]}>{suffix}</div>}
      </>
    );

    const sharedLinkProps = {
      ...(props as AnchorHTMLAttributes<HTMLAnchorElement>),
      className: buttonClasses,
      'data-testid': dataTestId,
      download,
      href: to,
      target,
    };

    return isExternal ? (
      <a {...sharedLinkProps}>{content}</a>
    ) : to && LinkComponent !== undefined ? (
      <LinkComponent {...sharedLinkProps}>{content}</LinkComponent>
    ) : (
      <button {...sharedButtonProps} className={buttonClasses}>
        {content}
      </button>
    );
  }
);
